import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';

import api from '@/services/apiSgft';
import { AllocationEditTypeAtom } from '@/state/AllocationEditType.atom';
import { AllocationTypeAtom } from '@/state/AllocationType.atom';
import { AllocationUpdateDataTypeAtom } from '@/state/UpdateDataType.atom';

import { removeTimeZone } from '../../utils/formatDate';
import { Button } from '../atoms/Button';
import Line from '../atoms/Line';
import SimpleSelect from '../atoms/SimpleSelect';
import SimpleTextField from '../atoms/SimpleTextField';
import SingleDate from '../atoms/SingleDate';
import SubtitleText from '../atoms/SubtitleText';
import ModalConfirmation from './ConfirmationModal';

interface Props {
	employeeId: number | undefined;
	name: string;
	currentAllocation: any;
	data: any;
	companiesList: {
		id: number;
		name: string;
		boards: {
			id: number;
			name: string;
			managements: {
				id: number;
				name: string;
				teamStructures: {
					id: number;
					name: string;
					team: {
						id: number;
						name: string;
					}[];
				}[];
			}[];
		}[];
	}[];
}

interface Option {
	value: number;
	label: string;
}

const compareString = (a: Option, b: Option) => {
	if (a.label < b.label) return -1;
	if (a.label > b.label) return 1;
	return 0;
};

function resolveAllocationManagementId(
	source: { management_id?: number } | null | undefined,
): number | undefined {
	const id = source?.management_id;
	if (id == null || id === 0) return undefined;
	return id;
}

const UpdateAllocationCard = ({
	employeeId,
	name,
	currentAllocation,
	companiesList,
	data,
}: Props) => {
	const [date, setDate] = useState('');
	const [justification, setJustification] = useState('');
	const [companyId, setCompanyId] = useState<string | number>('');
	const [boardId, setBoardId] = useState<string | number>('');
	const [managementId, setManagementId] = useState<string | number>('');
	const [teamStructureId, setTeamStructureId] = useState<string | number>('');
	const [teamId, setTeamId] = useState<string | number>('');
	const [isSavingData, setIsSavingData] = useState(false);
	const [type, setType] = useRecoilState(AllocationTypeAtom);
	const [isModalConfirmationOpen, setIsModalConfirmationOpen] = useState(false);
	const [editData, setEditData] = useRecoilState(AllocationEditTypeAtom);
	const [, setUpdateData] = useRecoilState(AllocationUpdateDataTypeAtom);

	const findHierarchyByManagementId = useCallback(
		(id: number) => {
			for (const company of companiesList) {
				for (const board of company.boards) {
					for (const management of board.managements) {
						if (management.id === id) {
							return { company, board, management };
						}
					}
				}
			}
			return null;
		},
		[companiesList],
	);

	const selectedCompany = companiesList.find(
		(company) => company.id === companyId,
	);
	const selectedBoard = selectedCompany?.boards.find(
		(board) => board.id === boardId,
	);
	const selectedManagement = selectedBoard?.managements.find(
		(management) => management.id === managementId,
	);
	const selectedTeamStructure = selectedManagement?.teamStructures.find(
		(teamStructure) => teamStructure.id === teamStructureId,
	);

	const companyValues: Option[] = companiesList
		.map((company) => ({ value: company.id, label: company.name }))
		.sort(compareString);

	const boardValues: Option[] = selectedCompany
		? selectedCompany.boards
				.map((board) => ({ value: board.id, label: board.name }))
				.sort(compareString)
		: [];

	const managementValues: Option[] = selectedBoard
		? selectedBoard.managements
				.map((management) => ({
					value: management.id,
					label: management.name,
				}))
				.sort(compareString)
		: [];

	const teamStructureValues: Option[] = selectedManagement
		? selectedManagement.teamStructures
				.map((teamStructure) => ({
					value: teamStructure.id,
					label: teamStructure.name,
				}))
				.sort(compareString)
		: [];

	const teams: Option[] = selectedTeamStructure
		? selectedTeamStructure.team
				.map((team) => ({ value: team.id, label: team.name }))
				.sort(compareString)
		: [];

	useEffect(() => {
		if (companiesList.length > 0 && currentAllocation) {
			const mid = resolveAllocationManagementId(currentAllocation);
			if (mid !== undefined) {
				const hierarchy = findHierarchyByManagementId(mid);
				if (hierarchy) {
					setCompanyId(hierarchy.company.id);
					setBoardId(hierarchy.board.id);
					setManagementId(hierarchy.management.id);
				}
			}
		}
	}, [companiesList, currentAllocation, findHierarchyByManagementId]);

	useEffect(() => {
		if (editData.isUpdated && type === 'edit') {
			setTeamId(editData.team_id);
			setDate(editData.start_date);
			const mid = resolveAllocationManagementId(editData);
			const hierarchy =
				mid !== undefined ? findHierarchyByManagementId(mid) : null;
			if (hierarchy) {
				setCompanyId(hierarchy.company.id);
				setBoardId(hierarchy.board.id);
				setManagementId(hierarchy.management.id);
				const teamStructureValue = hierarchy.management.teamStructures.find(
					(value) => value.name === editData.teamStructure,
				);
				if (teamStructureValue) setTeamStructureId(teamStructureValue.id);
				if (editData.justification) setJustification(editData.justification);
			}
		}
		if (type === 'deallocate') {
			setJustification(editData.justification);
			setDate(editData.end_date);
		}
	}, [editData, type, findHierarchyByManagementId]);

	const isAbleToSave =
		(managementId !== '' &&
			teamStructureId !== '' &&
			teamId !== '' &&
			justification !== '' &&
			date !== '') ||
		(type === 'deallocate' && justification !== '' && date !== '');

	const handleSaveData = () => {
		if (isAbleToSave) {
			if (type === 'deallocate') {
				setIsSavingData(true);
				api
					.post(
						`/team-employee/desallocate-employee?employeeId=${employeeId}&endDate=${removeTimeZone(new Date(date))}&justification=${justification}`,
					)
					.then((response: any) => {
						toast.success(response.data.message, {
							theme: 'colored',
							toastId: 'success',
						});
						setUpdateData(true);

						handleClose();
					})
					.catch((error) => {
						const errorMessage =
							error?.response?.status < 500
								? error?.response?.data?.message
								: 'Sua solicitação não foi processada. Tente novamente mais tarde!';
						toast.error(errorMessage, {
							theme: 'colored',
							toastId: 'error',
						});
					})
					.finally(() => {
						setIsSavingData(false);
					});
			} else {
				setIsSavingData(true);
				const method: 'post' | 'put' = type === 'create' ? 'post' : 'put';
				const data =
					type === 'create'
						? `?employeeId=${employeeId}&teamId=${teamId}&startDate=${removeTimeZone(new Date(date))}&justification=${justification}`
						: `?employeeTeamHistoryId=${editData.id}&teamId=${teamId}&startDate=${removeTimeZone(new Date(date))}&justification=${justification}`;

				api[method](`/team-employee${data}`)
					.then((response: any) => {
						toast.success(response.data.message, {
							theme: 'colored',
							toastId: 'success',
						});
						setUpdateData(true);
						handleClose();
					})
					.catch((error) => {
						const errorMessage =
							error?.response?.status < 500
								? error?.response?.data?.message
								: 'Sua solicitação não foi processada. Tente novamente mais tarde!';
						toast.error(errorMessage, {
							theme: 'colored',
							toastId: 'error',
						});
					})
					.finally(() => {
						setIsSavingData(false);
					});
			}
		}
	};

	const handleSaveButton = () => {
		const teamName = teams.find((team) => team.value === teamId);
		const teamStructureName = teamStructureValues.find(
			(teamStructure) => teamStructure.value === teamStructureId,
		);
		const managementName = managementValues.find(
			(management) => management.value === managementId,
		);
		const previousAllocation = findPreviousAllocation();
		if (
			teamName &&
			teamName.label === previousAllocation.team &&
			teamStructureName?.label === previousAllocation.teamStructure &&
			managementName?.label === previousAllocation.management &&
			(!editData || (editData && editData.id !== previousAllocation.id))
		) {
			toast.error(
				'A turma selecionada é a mesma turma à qual o funcionário já pertence na data da realocação',
				{
					theme: 'colored',
					toastId: 'error',
				},
			);
		} else {
			if (type === 'edit') {
				handleSaveData();
			} else if (type === 'create' || type === 'deallocate') {
				setIsModalConfirmationOpen(true);
			}
		}
	};

	const handleClose = () => {
		setType('');
		setJustification('');
		setTeamId('');
		setTeamStructureId('');
		const mid = resolveAllocationManagementId(currentAllocation);
		const hierarchy =
			mid !== undefined ? findHierarchyByManagementId(mid) : null;
		setCompanyId(hierarchy ? hierarchy.company.id : '');
		setBoardId(hierarchy ? hierarchy.board.id : '');
		setManagementId(hierarchy ? hierarchy.management.id : '');
		setDate('');
		setEditData({
			id: 0,
			isCurrent: false,
			employee_id: 0,
			end_date: '',
			justification: '',
			management: '',
			start_date: '',
			team: '',
			teamStructure: '',
			team_id: 0,
			isUpdated: false,
		});
	};

	const findPreviousAllocation = () => {
		let previousAllocation = data.find(
			(allocation: any) =>
				removeTimeZone(new Date(date)).getTime() >=
					removeTimeZone(new Date(allocation.start_date)).getTime() &&
				(allocation.end_date === undefined ||
					allocation.end_date === null ||
					removeTimeZone(new Date(date)).getTime() <=
						removeTimeZone(new Date(allocation.end_date)).getTime()),
		);

		if (
			!previousAllocation &&
			removeTimeZone(new Date(date)).getTime() >=
				removeTimeZone(new Date(data[0].end_date)).getTime()
		) {
			previousAllocation = data[0];
		}

		return previousAllocation;
	};

	const generateConfirmatiomMessage = () => {
		const teamName = teams.find((team) => team.value === teamId);
		const teamStructureName = teamStructureValues.find(
			(teamStructure) => teamStructure.value === teamStructureId,
		);
		const managementName = managementValues.find(
			(management) => management.value === managementId,
		);

		const previousAllocation = findPreviousAllocation();

		if (previousAllocation) {
			if (managementName?.label !== previousAllocation.management) {
				return `Tem certeza que deseja atualizar a alocação do colaborador ${name} da gerência ${previousAllocation.management} / ${previousAllocation.teamStructure} / ${previousAllocation.team} para a gerência ${managementName?.label} / ${teamStructureName?.label} / ${teamName && teamName.label}?`;
			} else if (
				teamStructureName?.label !== previousAllocation.teamStructure
			) {
				return `Tem certeza que deseja atualizar a alocação do colaborador ${name} da estrutura ${previousAllocation.teamStructure} / ${previousAllocation.team} para a estrutura ${teamStructureName?.label} / ${teamName && teamName.label}?`;
			} else {
				return `Tem certeza que deseja atualizar a alocação do colaborador ${name} da turma ${previousAllocation.team} para a turma ${teamName && teamName.label}?`;
			}
		} else {
			return `Tem certeza que deseja atualizar a alocação do colaborador ${name}?`;
		}
	};

	return (
		<div className="mb-5">
			{type !== '' && (
				<div>
					<SubtitleText
						subtitle={
							type === 'deallocate'
								? 'Desalocar colaborador da gerência'
								: type === 'create'
									? 'Atualizar Realocação'
									: 'Editar Troca'
						}
					/>
					<Line />
					{type === 'deallocate' ? (
						<div className="mb-3 flex w-full justify-between">
							<SimpleTextField
								width="w-[38rem]"
								fontColor="text-[#4a4a4a]"
								fontSize="text-[12px]"
								title="Justificativa"
								value={justification}
								setState={setJustification}
							/>
							<SingleDate
								id={1}
								dateValue={date}
								setDate={setDate}
								value="Data da desalocação"
								width="10rem"
							/>
						</div>
					) : (
						<>
							<div className="mb-3 flex w-full justify-between">
								<SimpleSelect
									width="14rem"
									title={'Empresa'}
									values={companyValues}
									value={companyId}
									setState={(id) => {
										setCompanyId(id as number);
										setBoardId('');
										setManagementId('');
										setTeamStructureId('');
										setTeamId('');
									}}
								/>
								<SimpleSelect
									width="16rem"
									title={'Diretoria'}
									values={companyId !== '' ? boardValues : []}
									value={boardId}
									disabled={companyId === ''}
									setState={(id) => {
										setBoardId(id as number);
										setManagementId('');
										setTeamStructureId('');
										setTeamId('');
									}}
								/>
								<SimpleSelect
									width="16rem"
									title={'Gerência'}
									values={boardId !== '' ? managementValues : []}
									value={managementId}
									disabled={boardId === ''}
									setState={(id) => {
										setManagementId(id as number);
										setTeamStructureId('');
										setTeamId('');
									}}
								/>
							</div>
							<div className="mb-3 flex w-full justify-between">
								<SimpleSelect
									width="20rem"
									title={'Estrutura'}
									values={managementId !== '' ? teamStructureValues : []}
									value={teamStructureId}
									disabled={managementId === ''}
									setState={(id) => {
										setTeamStructureId(id as number);
										setTeamId('');
									}}
								/>
								<SimpleSelect
									width="16rem"
									title={'Turma'}
									values={teamStructureId !== '' ? teams : []}
									value={teamId}
									disabled={teamStructureId === ''}
									setState={(id) => setTeamId(id as number)}
								/>
								<SingleDate
									id={1}
									dateValue={date}
									setDate={setDate}
									value="Data de início"
									width="10rem"
								/>
							</div>
							<div className="mb-3 flex w-full justify-between">
								<SimpleTextField
									width="w-full"
									fontColor="text-[#4a4a4a]"
									fontSize="text-[12px]"
									title="Justificativa"
									value={justification}
									setState={setJustification}
								/>
							</div>
						</>
					)}

					<div className="flex justify-between p-5">
						<Button
							type="button"
							title="Cancelar"
							variant="link"
							className="min-w-40  font-semibold ring-1"
							onClick={handleClose}
						>
							Cancelar
						</Button>
						<Button
							type="button"
							className="min-w-40  underline"
							onClick={handleSaveButton}
							isLoading={isSavingData}
							disabled={!isAbleToSave}
						>
							Salvar
						</Button>
					</div>
				</div>
			)}
			{isModalConfirmationOpen && (
				<ModalConfirmation
					title={''}
					description={
						type === 'deallocate'
							? `Tem certeza que deseja desalocar esse colaborador?`
							: generateConfirmatiomMessage()
					}
					confirmAction={handleSaveData}
					isOpen={isModalConfirmationOpen}
					onClose={() => setIsModalConfirmationOpen(false)}
					discartAction={() => setIsModalConfirmationOpen(false)}
				/>
			)}
		</div>
	);
};

export default UpdateAllocationCard;
